/* eslint-env browser */
import queueMicrotask from 'queue-microtask'
import { openDB } from 'idb'

import Debug from 'debug'

import IdbChunkStore from 'idb-chunk-store'
import MemoryChunkStore from 'memory-chunk-store'
// import FoundationChunkStore from '../foundation-chunk-store/index.js'
import FsAccessChunkStore from 'fs-access-chunk-store'
import { captureException, captureMessage } from './sentry.js'
import { logEvent, roundSizeAsMb } from './analytics.js'
import { browserDetect } from './browser-detect.js'

const debug = Debug('wormhole:storage')

// HACK: access indexedDB early to prevent idb open from hanging in Safari.
// See https://github.com/SocketDev/wormhole/issues/832 and
// https://bugs.webkit.org/show_bug.cgi?id=226547
const idbHackRef = globalThis.indexedDB

const OPEN_EXPIRATION_TIME_SECONDS = 60
const STORAGE_FAILURE_EXPIRATION_SECONDS = 24 * 3600 // 1 day; use memory if there was a quota error since then
const EXTRA_FREE_SPACE = 100_000_000 // Try to leave this much free space

export class StorageManifest {
  dbPromise = null
  openerId = null
  refreshInterval = null
  destroyed = false
  memoryOnly = false

  constructor () {
    // Used to detect if an entry is owned by the current tab
    const openerIdArray = new Uint8Array(16)
    crypto.getRandomValues(openerIdArray)
    this.openerId = Buffer.from(openerIdArray).toString('hex')

    if (!idbHackRef) {
      // HACK: this should not happen normally; it's just here to make sure idbHackRef
      // is used somewhere and not optimized out.
      // But if indexedDB is truly not present on globalThis, we fall back to memory
      // gracefully.
      this.memoryOnly = true
      return
    }

    this.dbPromise = (async () => {
      let db = null
      try {
        db = await openDB('storage-manifest', undefined, {
          upgrade: db => {
            db.createObjectStore('stores', {
              keyPath: 'name'
            })

            db.createObjectStore('failed-types', {
              keyPath: 'type'
            })
          },
          blocked: () => {
            captureMessage('StorageManifest open blocked')
            // Try to keep things working anyway
            this.memoryOnly = true
          },
          blocking: () => {
            captureMessage('StorageManifest open blocking')
          },
          terminated: () => {
            // This event only fires on *unexpected* close, not when .close() is called
            // Don't know why this is happening, so log it
            captureMessage('StorageManifest DB unexpectedly closed')

            // Try to keep things working anyway
            this.memoryOnly = true
            if (this.refreshInterval != null) {
              clearInterval(this.refreshInterval)
              this.refreshInterval = null
            }
          }
        })
      } catch (err) {
        console.log('IndexedDB not available')
        // Failed to open indexeddb; fall back to memory
        this.memoryOnly = true
        return null
      }

      // Enable only once db open was successful
      this.refresh()
      this.refreshInterval = setInterval(
        () => this.refresh(),
        (OPEN_EXPIRATION_TIME_SECONDS / 2) * 1000
      )

      return db
    })()
  }

  async getChunkStore (desiredName, size, pieceLength, expirationTimestampMs) {
    if (this.destroyed) {
      throw new Error('already destroyed')
    }

    const {
      manifestEntryHandle,
      recentFailure,
      browserReportedQuota
    } = await this.reserveSpace(
      desiredName,
      size,
      pieceLength,
      expirationTimestampMs
    )

    const { type } = manifestEntryHandle

    console.log(`Using ${type} storage`)
    logEvent('storage', {
      type,
      sizeMb: roundSizeAsMb(size),
      recentFailure,
      quotaMb: roundSizeAsMb(browserReportedQuota),
      memoryOnly: this.memoryOnly
    })

    const WrappedChunkStore = wrapChunkStore(
      {
        size,
        pieceLength
      },
      manifestEntryHandle
    )
    return {
      store: new WrappedChunkStore(),
      type
    }
  }

  async destroy () {
    if (this.destroyed) {
      throw new Error('already destroyed')
    }
    this.destroyed = true

    if (!this.memoryOnly) {
      await this.runTransactionOnEntries(async stores => {
        for (const store of stores) {
          store.setInUse(null)
        }
      })

      const db = await this.dbPromise
      db.close()
    }

    if (this.refreshInterval != null) {
      clearInterval(this.refreshInterval)
      this.refreshInterval = null
    }
    debug('destroyed storageManifest')
  }

  async refresh () {
    await this.runTransactionOnEntries(async entries => {
      debug('refreshing active stores')
      for (const entry of entries) {
        for (const opener of entry.data.openedBy) {
          if (opener.openerId === this.openerId) {
            opener.updatedAt = null // entry.normalize() will fill in now
            entry.dirty = true
          }
        }
      }
    })
  }

  async addFailedType (type) {
    if (this.destroyed) {
      throw new Error('already destroyed')
    }

    const db = await this.dbPromise
    await db.put('failed-types', {
      type,
      failedAtTimestamp: Date.now()
    })
  }

  async getFailedTypes () {
    const db = await this.dbPromise
    return await db.getAll('failed-types')
  }

  async runTransactionOnEntries (transactionFn) {
    const db = await this.dbPromise
    const transaction = db.transaction('stores', 'readwrite')
    const promises = [transaction.done]

    const rawEntries = await transaction.store.getAll()
    const purgeableEntries = []
    const liveEntries = []

    for (const rawEntry of rawEntries) {
      const entry = new StoreManifestEntry(this, rawEntry, false)
      entry.normalize()
      if (entry.purgeable) {
        purgeableEntries.push(entry)
      } else {
        liveEntries.push(entry)
      }
    }

    try {
      await transactionFn(liveEntries, purgeableEntries)
    } catch (err) {
      transaction.done.catch(() => {})
      // User provided function failed, so abort
      try {
        transaction.abort()
      } catch {}
      throw err
    }

    const toDelete = []
    for (const entry of liveEntries.concat(purgeableEntries)) {
      entry.normalize()
      promises.push(entry.save(transaction.store))
      if (entry.purgeable) {
        toDelete.push(entry)
      }
    }

    // Wait for transaction to complete
    await Promise.all(promises)

    const deleted = []
    for (const entry of toDelete) {
      try {
        await entry.destroyUnderlyingStore()
      } catch (err) {
        console.error(`Error destroying store: ${err.message}`)
        captureException(err)
      }

      // Assume the store actually got deleted even if destroyUnderlyingStore threw
      // TODO: there may be cases where that's a bad assumption.
      // Let's watch for quota errors. Perhaps try a few times?
      deleted.push(entry)
    }

    if (deleted.length > 0) {
      const db = await this.dbPromise
      const transaction = db.transaction('stores', 'readwrite')

      await Promise.all([
        transaction.done,
        ...deleted.map(entry => transaction.store.delete(entry.data.name))
      ])
    }
  }

  getChunkStoreType () {
    const { browser, os } = browserDetect()
    if (this.memoryOnly) {
      return 'memory'
      // Disabled pending https://github.com/SocketDev/sync/issues/116
      // } else if (typeof storageFoundation !== 'undefined') {
      //   return 'foundation'
    } else if (
      typeof navigator?.storage?.getDirectory === 'function' &&
      // Do not use Safari's buggy File System Access implementation
      browser !== 'safari' &&
      os !== 'ios'
    ) {
      // 'webfs' is the old name, but keep it for compatibility
      // with existing manifests
      return 'webfs'
    } else {
      return 'indexeddb'
    }
  }

  // Atomically reserves space, clearing old stuff if needed.
  async reserveSpace (name, size, pieceLength, expirationTimestampMs) {
    await this.dbPromise // Set memoryOnly if indexeddb doesn't work
    let type = this.getChunkStoreType()
    let recentFailure = false
    if (type !== 'memory') {
      // Check if there were any recent failures
      const now = Date.now()
      const failedTypes = await this.getFailedTypes()
      // If any (non-memory) storage failed recently use memory
      if (
        failedTypes.some(
          ({ failedAtTimestamp }) =>
            now - failedAtTimestamp < STORAGE_FAILURE_EXPIRATION_SECONDS * 1000
        )
      ) {
        console.log('Recent storage failure reported; falling back to memory')
        type = 'memory'
        recentFailure = true
      }
    }

    if (type === 'memory') {
      return {
        manifestEntryHandle: new StorageManifestEntryHandle(name, type, this),
        recentFailure,
        browserReportedQuota: null
      }
    }

    let quota = 1_000_000_000 // Default to 1GB, which is just below Safari's initial prompt level of 1.1GB
    let browserReportedQuota = null
    let browserReportedUsed = null
    if (typeof navigator?.storage?.estimate === 'function') {
      const estimate = await navigator.storage.estimate()
      debug(`browser reported storage quota: ${estimate.quota}`)
      debug(`browser reported storage used: ${estimate.usage}`)
      browserReportedQuota = estimate.quota
      quota = estimate.quota
      browserReportedUsed = estimate.usage
    }
    quota -= EXTRA_FREE_SPACE // Leave a bit of extra space

    await this.runTransactionOnEntries(async (liveEntries, deadEntries) => {
      let usedSize = 0
      for (const entry of liveEntries) {
        if (entry.data.name === name) {
          // See if we can reuse the existing store
          if (
            entry.data.size === size &&
            entry.data.pieceLength === pieceLength &&
            entry.data.type === type &&
            !entry.data.openedBy.some(opener => opener.mode === 'readwrite')
          ) {
            debug('reusing existing store')
            entry.setInUse('readwrite')
            return
          }

          // There is a conflict; choose a different name
          const nameSuffixArray = new Uint8Array(16)
          crypto.getRandomValues(nameSuffixArray)
          const nameSuffix = Buffer.from(nameSuffixArray).toString('hex')
          name = `${name}-${nameSuffix}`
          debug('renaming store to avoid confilict')
        }

        usedSize += entry.data.size
      }

      let deadUsedSize = 0
      for (const entry of deadEntries) {
        deadUsedSize += entry.data.size
      }

      if (
        browserReportedUsed != null &&
        browserReportedUsed > usedSize + deadUsedSize
      ) {
        // There is more used space than we can account for with the manifest.
        const usedSpaceNotInManifest =
          browserReportedUsed - usedSize - deadUsedSize

        // Add that to what we think we are still using
        usedSize += usedSpaceNotInManifest
      }

      debug(`quota: ${quota}`)
      debug(`total used: ${usedSize}`)

      if (usedSize + size > quota) {
        // Won't fit. Need to clean out some junk!

        // Sort by oldest expiration first
        liveEntries.sort(
          (left, right) =>
            left.data.expirationTimestampMs - right.data.expirationTimestampMs
        )

        for (const entry of liveEntries) {
          if (entry.data.openedBy.length === 0) {
            usedSize -= entry.data.size
            debug(`preparing to delete ${entry.data.name}`)
            entry.makePurgeable()

            if (usedSize + size <= quota) {
              break
            }
          }
        }

        // Only check here if we have a real quota (indicated by browserReportedUsed being defined)
        if (browserReportedUsed != null && usedSize + size > quota) {
          // Still not enough space!
          console.log('Not enough space; falling back to memory')
          type = 'memory'
          return
        }
      }

      liveEntries.push(
        new StoreManifestEntry(this, {
          name,
          expirationTimestampMs,
          size,
          pieceLength,
          type,
          openedBy: [
            {
              openerId: this.openerId,
              mode: 'readwrite'
            }
          ]
        })
      )
    })

    return {
      manifestEntryHandle: new StorageManifestEntryHandle(name, type, this),
      recentFailure,
      browserReportedQuota
    }
  }
}

// A single entry in the manifest
class StoreManifestEntry {
  purgeable = false // Should this be marked for deletion?

  constructor (manifest, data, dirty = true) {
    this.manifest = manifest
    this.data = data // Does this need to be written back to the DB?
    this.dirty = dirty
  }

  /*
   * Cleans up expired openedBy, and marks this entry as deletable
   * if it is expired
   */
  normalize () {
    const now = Date.now()
    // Remove expired openedBy entries
    const openedBy = this.data.openedBy.filter(opener => {
      // Fill in now for new entries
      if (opener.updatedAt == null) {
        this.dirty = true
        opener.updatedAt = now
        return true
      }

      // Always keep our own
      if (opener.openerId === this.manifest.openerId) {
        return true
      }

      // Keep others if they were updated recently
      return opener.updatedAt + OPEN_EXPIRATION_TIME_SECONDS * 1000 >= now
    })

    const shouldDelete = this.data.expirationTimestampMs < now
    if (shouldDelete) {
      this.data.openedBy = [] // Empty existing entries
      this.makePurgeable()
    } else if (this.data.openedBy.length !== openedBy.length) {
      this.dirty = true
      this.data.openedBy = openedBy
    }
  }

  /*
   * Sets this tab as using the store in the specified `mode`
   */
  setInUse (mode) {
    const openedBy = this.data.openedBy

    const index = openedBy.findIndex(
      opener => opener.openerId === this.manifest.openerId
    )
    if (mode === null) {
      // Should remove if exists
      if (index !== -1) {
        // Remove unwanted entry
        openedBy.splice(index, 1)
        this.dirty = true
      }
    } else if (mode === 'readwrite' || mode === 'readonly') {
      // Should add or update
      if (index !== -1 && openedBy[index].mode !== mode) {
        // Update existing entry
        openedBy[index].mode = mode
        this.dirty = true
      } else if (index === -1) {
        // Add new entry
        openedBy.push({
          openerId: this.manifest.openerId,
          mode: mode
        })
        this.dirty = true
      }
    } else {
      throw new Error(`Unexpected mode: ${mode}`)
    }
  }

  /*
   * Marks this entry as ready to delete
   */
  makePurgeable () {
    if (this.data.openedBy.length > 0) {
      throw new Error('Cannot makePurgeable while entry is in use')
    }

    this.purgeable = true
    this.dirty = true
    this.data.openedBy.push({
      openerId: this.manifest.openerId,
      mode: 'readwrite'
    })
  }

  /*
   * Saves this entry if needed
   */
  async save (transactionStore) {
    if (this.dirty) {
      await transactionStore.put(this.data)
    }
  }

  /*
   * Frees underlying storage corresponding
   * to this entry
   */
  async destroyUnderlyingStore () {
    console.log(`Deleting store: ${this.data.name} of type: ${this.data.type}`)
    const ChunkStoreConstructor = constructors[this.data.type]

    // Construct the store just to destroy it
    const store = new ChunkStoreConstructor(this.data.pieceLength, {
      name: this.data.name
    })
    await new Promise((resolve, reject) => {
      store.destroy(err => {
        if (err) {
          reject(err)
        } else {
          resolve()
        }
      })
    })
    console.log(
      `Finished deleting store: ${this.data.name} of type: ${this.data.type}`
    )
  }
}

// Handle used to update an entry from outside StorageManifest class
class StorageManifestEntryHandle {
  quotaExceededErrorFired = false
  quotaExceededErrorRecorded = false

  constructor (name, type, storageManifest) {
    this.name = name
    this.type = type
    this.storageManifest = storageManifest
  }

  async onError (err) {
    if (!err.notFound) {
      console.error(`Store error: ${err.message}`)
      captureException(err)
    }

    if (this.type === 'memory') {
      return
    }
    if (!isQuotaExceededError(err)) {
      return
    }

    // Don't actually try to record the error yet, since storage is full.
    // Wait until the offending store is deleted first
    this.quotaExceededErrorFired = true
  }

  async recordQuotaExceededError () {
    if (this.quotaExceededErrorFired && !this.quotaExceededErrorRecorded) {
      this.quotaExceededErrorRecorded = true
      await this.storageManifest.addFailedType(this.type)
    }
  }

  async removeFromManifest () {
    if (this.type === 'memory') {
      return
    }

    await this.storageManifest.runTransactionOnEntries(async entries => {
      for (const [i, entry] of entries.entries()) {
        if (entry.data.name === this.name) {
          entries.splice(i, 1)
        }
      }
    })
  }

  // useType can be 'readwrite', 'readonly', null (unused)
  async setInUse (useType) {
    if (this.type === 'memory') {
      return
    }

    await this.storageManifest.runTransactionOnEntries(async entries => {
      for (const entry of entries) {
        if (entry.data.name === this.name) {
          entry.setInUse(useType)
          return
        }
      }
    })
  }
}

export function isQuotaExceededError (err) {
  if (
    err instanceof globalThis.DOMException &&
    err.name === 'QuotaExceededError'
  ) {
    return true
  }

  // For Firefox
  if (err.name === 'NS_ERROR_FILE_NO_DEVICE_SPACE') {
    return true
  }

  return false
}

const constructors = {
  // foundation: FoundationChunkStore,
  webfs: FsAccessChunkStore,
  indexeddb: IdbChunkStore,
  memory: MemoryChunkStore
}

function wrapChunkStore (config, manifestEntryHandle) {
  const { size, pieceLength } = config
  const { name, type } = manifestEntryHandle

  const ChunkStoreConstructor = constructors[type]

  return class ChunkStoreWrapper extends ChunkStoreConstructor {
    _filled = false

    constructor () {
      try {
        super(pieceLength, {
          length: size,
          name
        })
      } catch (err) {
        manifestEntryHandle.onError(err)
        throw err
      }
    }

    // Called to indicate that all writes are finished and the chunk store is usable from other tabs
    async onFilled () {
      if (!this._filled) {
        this._filled = true

        // Foundation can only be used by one tab at once
        if (type !== 'foundation') {
          await manifestEntryHandle.setInUse('readonly')
        }
      }
    }

    put (index, buf, cb = () => {}) {
      if (this._filled) {
        queueMicrotask(() => {
          cb(new Error('Already filled; store is read-only'))
        })
        return
      }

      super.put(index, buf, err => {
        if (err) {
          manifestEntryHandle.onError(err)
        }

        cb(err)
      })
    }

    get (index, opts, cb) {
      if (!cb) {
        cb = opts
        opts = undefined
      }

      super.get(index, opts, (err, buf) => {
        if (err) {
          manifestEntryHandle.onError(err)
        }

        cb(err, buf)
      })
    }

    close (cb = () => {}) {
      super.close(async err => {
        if (err) {
          manifestEntryHandle.onError(err)
        }

        if (!manifestEntryHandle.quotaExceededErrorFired) {
          // This would fail if the quota is already exceeded
          await manifestEntryHandle.setInUse(null)
        }
        cb(err)
      })
    }

    destroy (cb = () => {}) {
      if (!this._filled || manifestEntryHandle.quotaExceededErrorFired) {
        // Not useable by other tabs; just destroy immediately
        super.destroy(err => {
          // This needs to be delayed until after destroy to ensure there is space to record the error
          manifestEntryHandle.recordQuotaExceededError()
          if (err) {
            // It may be still using storage, so leave it in the manifest
            manifestEntryHandle.setInUse(null).then(() => cb(null), cb)
          } else {
            manifestEntryHandle.removeFromManifest().then(() => cb(null), cb)
          }
        })
      } else {
        // May already be in use elsewhere
        this.close(cb)
      }
    }
  }
}
